import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Badge,
  LightModeSemanticColors,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { useFormatKomaRanges } from '~/components/common/GcExcludedKomaMark/useFormatKomaRanges'
import { Modal } from '~/components/common/Modal'
import { iconSize } from '~/constants/iconSize'

type Props = {
  /** 作成時にそろえたコマ番号 (1-origin, 1..48, 昇順)。空配列なら何も描画しない。 */
  alignedKomaNumbers: number[]
}

/**
 * 作成の時点で GC 済みだったコマの計画値を前回受理値 (無ければ 0) にそろえたことを
 * 示す chip (AC-PLC-037-2)。 `GcExcludedKomaMark` と同じ chip + modal の形で、
 * modal はそろえたコマの範囲と理由だけを出す (送信コマ欄は持たない)。
 * 空かどうかの判定は backend の値をそのまま使い、frontend で追加判定しない。
 */
export const GcCreationAlignedKomaMark = ({
  alignedKomaNumbers,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const formatKomaRanges = useFormatKomaRanges()

  if (alignedKomaNumbers.length === 0) {
    return null
  }

  const alignedText = formatKomaRanges(alignedKomaNumbers)
  const reason = t('page:planSubmission.gcCreationAlignedKoma.reason')

  return (
    <>
      <Tooltip content={reason}>
        {/* [DS差異] GcExcludedKomaMark と同じ。DS Badge は clickable な as prop を
            公開していないため、native button で Badge をラップする。 */}
        <button
          onClick={() => setIsOpen(true)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            marginLeft: '4px',
            padding: 0,
            verticalAlign: 'middle',
          }}
          type="button"
        >
          <Badge status="warning">
            <span
              style={{
                alignItems: 'center',
                display: 'inline-flex',
                gap: '2px',
              }}
            >
              <FaTriangleExclamationSolid
                boxSize={iconSize.badgeIcon}
                color={LightModeSemanticColors.Text.warningHigh.value}
                aria-hidden="true"
              />
              {t('page:planSubmission.gcCreationAlignedKoma.chipLabel')}
            </span>
          </Badge>
        </button>
      </Tooltip>
      <Modal
        footer={
          <TertiaryButton onClick={() => setIsOpen(false)}>
            {t('common:action.close')}
          </TertiaryButton>
        }
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        size="sm"
        title={t('page:planSubmission.gcCreationAlignedKoma.modalTitle')}
      >
        {/* 定義リスト: DS に定義リスト component が無く、design `#pr-gc-modal` も
            生の <dl> を使うため GcExcludedKomaMark と同じ semantics にする。 */}
        <dl
          style={{
            display: 'grid',
            gap: '8px 12px',
            gridTemplateColumns: '7.5rem 1fr',
            margin: 0,
          }}
        >
          <Text as="dt" color="Text.highEmphasis" fontWeight="semibold">
            {t('page:planSubmission.gcCreationAlignedKoma.alignedLabel')}
          </Text>
          <Text as="dd" margin={0} color="Text.highEmphasis">
            {alignedText}
          </Text>
        </dl>
        <Text marginTop={3} color="Text.midEmphasis" fontSize="sm">
          {reason}
        </Text>
      </Modal>
    </>
  )
}
